import { useMutation, UseMutationResult } from 'react-query';

import {
  reportUploadFailed,
  uploadInParts
} from '@utils/api/sessions/multipart-upload';

interface UploadToAzureBlobPayload {
  uploadUrl: string;
  file: File;
  sessionId: string;
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
}

// Single PUT. Kept only as the last resort for a backend that can offer nothing
// better, because it is all-or-nothing: a drop at 90% loses the whole file and
// leaves nothing staged to resume from or even diagnose.
//
// S3 sessions no longer come here — see uploadViaMultipart. This used to be
// their only path, which is how the S3 cutover silently took resumability away
// from every customer it moved: 8,398 of 8,712 issued upload URLs failed over
// three days, on links where the chunked Azure path was completing fine.
const uploadViaPresignedPut = (
  payload: UploadToAzureBlobPayload
): Promise<void> =>
  new Promise((resolve, reject) => {
    const { uploadUrl, file, onProgress, sessionId, signal } = payload;
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader(
      'Content-Type',
      file.type || 'application/octet-stream'
    );

    let loaded = 0;
    xhr.upload.onprogress = ev => {
      loaded = ev.loaded;
      if (onProgress && ev.lengthComputable) {
        onProgress(Math.round((ev.loaded / ev.total) * 100));
      }
    };

    // Tell the server what we saw. Without it a failed PUT is silence, and
    // silence is what the session-timeout sweep later reports to the customer
    // as "this session timed out before processing could begin" — a message
    // about our queue, for a failure that happened on their connection.
    const failed = (error: Error, httpStatus?: number) => {
      if (sessionId) {
        void reportUploadFailed(sessionId, {
          httpStatus,
          bytesSent: loaded,
          fileSize: file.size,
          errorMessage: error.message
        });
      }
      reject(error);
    };

    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : failed(
            new Error(
              `Upload failed (HTTP ${xhr.status}): ${xhr.responseText}`
            ),
            xhr.status
          );
    xhr.onerror = () =>
      failed(new Error('Upload failed: the connection was interrupted'));
    xhr.onabort = () => failed(new Error('Upload cancelled'));

    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(file);
  });

// OVH S3 multipart upload: the file goes up in parts, each
// part retries on its own, and an interrupted upload resumes from where it
// stopped rather than from zero.
//
// If the server cannot offer the chunked flow — an older API, or a backend
// without multipart — this falls back to the plain PUT rather than failing the
// upload outright. That fallback is strictly worse on a slow link, so it is a
// fallback and not a choice.
const uploadViaMultipart = async (
  payload: UploadToAzureBlobPayload
): Promise<void> => {
  const { sessionId, file, onProgress, signal } = payload;

  try {
    await uploadInParts({ sessionId, file, onProgress, signal });
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    const unsupported = status === 404 || status === 400;
    if (!unsupported || signal?.aborted) throw error;

    await uploadViaPresignedPut(payload);
  }
};

const uploadToAzureBlob = async (
  payload: UploadToAzureBlobPayload
): Promise<void> => {
  if (payload.sessionId) return uploadViaMultipart(payload);

  // No session id means multipart endpoints cannot be addressed. Keep the
  // generic presigned PUT fallback for on-prem-compatible callers.
  return uploadViaPresignedPut(payload);
};

export const useUploadToAzureBlob = (): UseMutationResult<
  void,
  Error,
  UploadToAzureBlobPayload
> => {
  return useMutation(uploadToAzureBlob);
};
